import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as reportsActions } from '../../actions/reportsActions';
import {
    REPORT_IDS,
    REPORT_URL_PARAMS as URLParams,
    REPORT_EXPORT_FILE_TYPES
} from '../../constants/constants';
import Routes from '../../constants/routes';
import * as types from '../../constants/actionTypes';
import Notification from '../Common/Notification';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import PageContainer from '../Common/PageContainer';
import Card from '../Common/Card';
import Icon from '../Common/Icon';
import FileTypeIcon from '../Common/FileTypeIcon';
import InputText from '../Common/InputText';
import './Reports.scss';
import ReportFiltersManager from './ReportFiltersManager';
import ReportDataHelper from './ReportDataHelper';
import URLSearchParams from '@ungap/url-search-params'; // URLSearchParams polyfill for IE11

export class ReportViewPage extends React.Component {

    constructor(props, context) {
        super(props, context);

        this.reportDataHelper = new ReportDataHelper(props.reportsActions);

        this.state = {
            isLoading: false,
            filters: {},
            report: {},
            allowTaskListExportToExcel: true,
            isFormValid: true,
            reportPreviewBody: '',
            numberOfPages: 1,
            pageNumber: 1,
            selectedProject :  ''
        };
    }

    /**
     * Any time we view or export the report, we should wipe out the URL params,
     * or they will still be there to override the selected values when the
     * report filters component is mounted again.
     */
    clearUrlParams = () => {
        this.props.history.push(this.props.history.location.pathname);
    }

    async componentDidMount() {
        document.title = 'Report';
        this.setState({
            ...this.state,
            selectedProject: this.props.history.location?.state?.projectName,
        });

        await this.loadReportAndFilters();

        //only auto-execute the report if it is the task list rpt with params
        if (this.props.reportId === 1 && this.props.paramCount > 0) {
            await this.onViewReportClick();
        }
    }

    loadReportAndFilters = async () => {
        try {
            this.setState({ isLoading: true });
            const response = await this.props.reportsActions.loadReportFilters(this.props.reportId);
            if (response.type === types.LOAD_REPORT_FILTERS_SUCCESS) {
                this.setState({
                    isLoading: false,
                    filters: (response.payload.filters) ? response.payload.filters : {},
                    report: response.payload.report,
                    allowTaskListExportToExcel: response.payload.allowTaskListExportToExcel
                });
                document.title = `Report - ${response.payload.report.name}`;
            } else if (response.type === types.LOAD_REPORT_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    loadLocationReponsibleUsers = async (locationKeys) => {
        try {
            const response = await this.props.reportsActions.loadLocationResponsibleUsers(locationKeys);
            if (response.type === types.LOAD_REPORT_LOCATION_RESPONSIBLE_USERS_SUCCESS) {
                this.setState({
                    isLoading: false,
                    filters: {
                        ...this.state.filters,
                        responsibleUsers: response.payload.filters.responsibleUsers
                    }
                });
            } else if (response.type === types.LOAD_REPORT_LOCATION_RESPONSIBLE_USERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    loadProjectDependentFilters = async (projectId) => {
        try {
            const response = await this.props.reportsActions.loadProjectDependentFilters(this.state.report.id, projectId);
            if (response.type === types.LOAD_REPORT_PROJECT_DEPENDENT_FILTERS_SUCCESS) {
                this.setState({
                    isLoading: false,
                    filters: {
                        ...this.state.filters,
                        projectBaselines: response.payload.filters.projectBaselines,
                        tasks: response.payload.filters.tasks,
                        projectZeroDates: response.payload.filters.projectZeroDates,
                    }
                });
            } else if (response.type === types.LOAD_REPORT_PROJECT_DEPENDENT_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    loadLocationsForCoachFilters = async (coachId) => {
        try {
            const response = await this.props.reportsActions.loadLocationsForCoach(coachId);
            if (response.type === types.LOAD_COACH_LOCATIONS_FILTERS_SUCCESS) {
                this.setState({
                    isLoading: false,
                    filters: {
                        ...this.state.filters,
                        coachLocations: response.payload.filters.locations
                    }
                });
            } else if (response.type === types.LOAD_COACH_LOCATIONS_FILTERS_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    onViewReportClick = async () => {
        try {
            if (!this.reportDataHelper.validateForm(this.props.reportId)) {
                this.setState({ isFormValid: false });
                return;
            }

            if (this.state.pageNumber < 1 || this.state.pageNumber > this.state.numberOfPages) {
                Notification("error", "Error", `Page number must be between 1 and ${this.state.numberOfPages}`);
                return;
            }

            this.setState({ isLoading: true, isFormValid: true });
            this.clearUrlParams();
            const reportRequest = this.reportDataHelper.mapToApiRequest(this.props.reportId);
            const request = {
                ...reportRequest,
                pageNumber: this.state.pageNumber
            }
            const response = await this.props.reportsActions.viewReport(request);
            if (response.type === types.VIEW_REPORT_SUCCESS) {
                this.setState({
                    isLoading: false,
                    reportPreviewBody: response.payload.reportResponse.body,
                    numberOfPages: response.payload.reportResponse.numberOfPages,
                    pageNumber: response.payload.reportResponse.pageNumber,
                });
            } else if (response.type === types.VIEW_REPORT_FAILURE) {
                Notification("error", "Error", response.payload.message);
                this.setState({ isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    onClearFiltersClick = () => {
        this.reportDataHelper.clearForm(this.props.reportId);
    }

    onScheduleReportButtonClick = () => {
        // Call mapToApiRequest to ensure we save the state of the entered filters
        // before sending the person to the schedule report page, which will load
        // the same filters form with all the same filter values pre-selected.
        this.reportDataHelper.mapToApiRequest(this.state.report.id);
        this.onScheduleReportLinkClick();
    }

    onScheduleReportLinkClick = () => {
        const route = `${Routes.ScheduleReport}/${this.state.report.id}`;
        this.props.history.push(route);
    }

    exportReport = async (exportFileType) => {
        try {
            if (!this.reportDataHelper.validateForm(this.props.reportId)) {
                this.setState({ isFormValid: false });
                return;
            }
            this.setState({ isLoading: true, isFormValid: true });
            this.clearUrlParams();
            const request = this.reportDataHelper.mapToApiRequest(this.props.reportId);
            request.exportFileType = exportFileType;
            await this.props.reportsActions.exportReport(request);
            this.setState({ isLoading: false });
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    }

    onFirst = () => {
        if (this.state.pageNumber.toString() !== "1") {
            this.setState({ pageNumber: 1 }, this.onViewReportClick);
        }
    }

    onPrev = () => {
        const pageNumber = (this.state.pageNumber > 1) ? this.state.pageNumber - 1 : 1;
        if (pageNumber.toString() !== this.state.pageNumber.toString()) {
            this.setState({ pageNumber: pageNumber }, this.onViewReportClick);
        }
    }

    onNext = () => {
        const pageNumber = (this.state.pageNumber < this.state.numberOfPages) ? this.state.pageNumber + 1 : this.state.numberOfPages;
        if (pageNumber.toString() !== this.state.pageNumber.toString()) {
            this.setState({ pageNumber: pageNumber }, this.onViewReportClick);
        }
    }

    onLast = () => {
        if (this.state.pageNumber.toString() !== this.state.numberOfPages.toString()) {
            this.setState({ pageNumber: this.state.numberOfPages }, this.onViewReportClick);
        }
    }

    onPageNumberChange = (e) => {
        const pageNumber = parseInt(e.target.value);
        if (pageNumber || e.target.value === "") {
            this.setState({ pageNumber: e.target.value });
        }
    }

    onPageNumberBlur = () => {
        this.onViewReportClick();
    }

    onPageNumberKeyUp = (e) => {
        if (e.keyCode == 13) {
            this.onViewReportClick();
        }
    }

    renderFilters = () => {
        const report = this.state.report;
        const filters = this.state.filters;
        const urlParams = this.props.urlParams;
        const savedFilterValues = this.props.savedFilterValues;
        const isFormValid = this.state.isFormValid;
        const registerReportFilterFunctions = this.reportDataHelper.registerReportFilterFunctions;
        const onLocationChange = this.loadLocationReponsibleUsers;
        const onProjectChange = this.loadProjectDependentFilters;
        const onCoachChange = this.loadLocationsForCoachFilters;
        const isSchedulingMode = false;

        return (
            <ReportFiltersManager
                report={report}
                filters={filters}
                urlParams={urlParams}
                savedFilterValues={savedFilterValues}
                isFormValid={isFormValid}
                registerReportFilterFunctions={registerReportFilterFunctions}
                onLocationChange={onLocationChange}
                onProjectChange={onProjectChange}
                onCoachChange={onCoachChange}
                isSchedulingMode={isSchedulingMode}
                selectedProject={this.state.selectedProject}
            />
        );
    }

    /**
     * Logic wrapper for determining if this is one of the reports that disallows
     * export to excel based on a user's profile setting, which was returned to
     * us via the loadReportAndFilters call
     */
    isExcelAllowed = () => {
        const reportId = this.state.report.id;
        const isTaskListOrPastDueTasks = (reportId === REPORT_IDS.TaskList || reportId === REPORT_IDS.PastDueTaskList);

        return (isTaskListOrPastDueTasks && this.state.allowTaskListExportToExcel && this.state.report.isExcelAllowed)
            || (!isTaskListOrPastDueTasks && this.state.report.isExcelAllowed);
    }

    render() {
        const showPreviewBody = this.state.reportPreviewBody.trim().length > 0;
        const showPreviewHeader = showPreviewBody;
        const showPaging = showPreviewHeader && this.state.numberOfPages && this.state.numberOfPages > 1;

        const showViewReportButton = this.state.report.isHtmlAllowed;
        const showExcelExportButton = !showViewReportButton && this.isExcelAllowed();
        const showPdfExportButton = !showViewReportButton && this.state.report.isPdfAllowed;
        const showScheduleReportButton = !showViewReportButton;

        const showScheduleReportLink = showPreviewBody;
        const showExcelLink = showPreviewBody && this.isExcelAllowed();
        const showPfdLink = showPreviewBody && this.state.report.isPdfAllowed;

        return (
            <PageContainer className="report-view-page">
                <LoadingIndicator isLoading={this.state.isLoading} isFullscreen={true}>
                    <Card title={this.state.report.name}>
                        {this.renderFilters()}
                        <div className="form-buttons">
                            {
                                showViewReportButton &&
                                <button type="button" className="btn btn-primary" onClick={this.onViewReportClick}>View Report</button>
                            }
                            {
                                showExcelExportButton &&
                                <button type="button" className="btn btn-secondary" onClick={() => this.exportReport(REPORT_EXPORT_FILE_TYPES.Excel)}>Excel Export</button>
                            }
                            {
                                showPdfExportButton &&
                                <button type="button" className="btn btn-secondary" onClick={() => this.exportReport(REPORT_EXPORT_FILE_TYPES.Pdf)}>Pdf Export</button>
                            }
                            {
                                showScheduleReportButton &&
                                <button type="button" className="btn btn-secondary" onClick={this.onScheduleReportButtonClick}>Schedule Report</button>
                            }
                            <button type="button" className="btn btn-light" onClick={this.onClearFiltersClick}>Clear Filters</button>
                        </div>
                        {
                            showPreviewHeader &&
                            <div className="preview-header">
                                <div className="links">
                                    {
                                        showScheduleReportLink &&
                                        <button type="button" className="btn btn-link schedule-report-link" onClick={this.onScheduleReportLinkClick}><Icon name="calendar-alt" />Schedule Report</button>
                                    }
                                    {
                                        showExcelLink &&
                                        <button type="button" className="btn btn-link excel-link" onClick={() => this.exportReport(REPORT_EXPORT_FILE_TYPES.Excel)}><FileTypeIcon extension=".xlsx" />Excel</button>
                                    }
                                    {
                                        showPfdLink &&
                                        <button type="button" className="btn btn-link pdf-link" onClick={() => this.exportReport(REPORT_EXPORT_FILE_TYPES.Pdf)}><FileTypeIcon extension=".pdf" />PDF</button>
                                    }
                                </div>
                                {
                                    showPaging &&
                                    <div className="paging">
                                        <button type="button" className="btn btn-secondary" onClick={this.onFirst}>First &lt;&lt;</button>
                                        <button type="button" className="btn btn-secondary" onClick={this.onPrev}>Prev &lt;</button>
                                        <span className="legend">
                                            <InputText name="pageNumber" type="text" value={this.state.pageNumber.toString()} onChange={this.onPageNumberChange} onBlur={this.onPageNumberBlur} onKeyUp={this.onPageNumberKeyUp} />
                                            <span className="total">of {this.state.numberOfPages}</span>
                                        </span>
                                        <button type="button" className="btn btn-secondary" onClick={this.onNext}>Next &gt;</button>
                                        <button type="button" className="btn btn-secondary" onClick={this.onLast}>Last &gt;&gt;</button>
                                    </div>
                                }
                            </div>
                        }
                        {
                            showPreviewBody &&
                            <div className="report-preview-wrapper" dangerouslySetInnerHTML={{ __html: this.state.reportPreviewBody }}>
                            </div>
                        }
                        {
                            /**
                             * This is not used anywhere else, other than this page, and all the functionality of exporting, viewing,
                             * scheduling, blurring, clicking paging buttons, and hitting enter has to be here, so this preview header
                             * is simply listed twice, above and below the preview body, instead of being abstracted into a nearly
                             * featureless component.
                             */
                            showPreviewHeader &&
                            <div className="preview-header">
                                <div className="links">
                                    {
                                        showScheduleReportLink &&
                                        <button type="button" className="btn btn-link schedule-report-link" onClick={this.onScheduleReportLinkClick}><Icon name="calendar-alt" />Schedule Report</button>
                                    }
                                    {
                                        showExcelLink &&
                                        <button type="button" className="btn btn-link excel-link" onClick={() => this.exportReport(REPORT_EXPORT_FILE_TYPES.Excel)}><FileTypeIcon extension=".xlsx" />Excel</button>
                                    }
                                    {
                                        showPfdLink &&
                                        <button type="button" className="btn btn-link pdf-link" onClick={() => this.exportReport(REPORT_EXPORT_FILE_TYPES.Pdf)}><FileTypeIcon extension=".pdf" />PDF</button>
                                    }
                                </div>
                                {
                                    showPaging &&
                                    <div className="paging">
                                        <button type="button" className="btn btn-secondary" onClick={this.onFirst}>First &lt;&lt;</button>
                                        <button type="button" className="btn btn-secondary" onClick={this.onPrev}>Prev &lt;</button>
                                        <span className="legend">
                                            <InputText name="pageNumber" type="text" value={this.state.pageNumber.toString()} onChange={this.onPageNumberChange} onBlur={this.onPageNumberBlur} onKeyUp={this.onPageNumberKeyUp} />
                                            <span className="total">of {this.state.numberOfPages}</span>
                                        </span>
                                        <button type="button" className="btn btn-secondary" onClick={this.onNext}>Next &gt;</button>
                                        <button type="button" className="btn btn-secondary" onClick={this.onLast}>Last &gt;&gt;</button>
                                    </div>
                                }
                            </div>
                        }
                    </Card>
                </LoadingIndicator>
            </PageContainer>
        );
    }
}

ReportViewPage.propTypes = {
    reportId: PropTypes.number.isRequired,
    savedFilterValues: PropTypes.object,
    urlParams: PropTypes.object.isRequired,
    paramCount: PropTypes.number.isRequired,
    history: PropTypes.object.isRequired,
    reportsActions: PropTypes.object.isRequired
};

function mapStateToProps(state, ownProps) {
    let reportId = null;

    if (ownProps.match.params.reportId) {
        reportId = Number.parseInt(ownProps.match.params.reportId);
    }

    let savedFilterValues = {};
    if (state.reports && state.reports.filterModels && reportId && state.reports.filterModels[reportId]) {
        savedFilterValues = state.reports.filterModels[reportId];
    }

    const searchParams = new URLSearchParams(ownProps.location.search);

    let urlParams = {};

    if (searchParams.toString().length > 0) {
        urlParams = {
            locationKeys: searchParams.getAll(URLParams.LocationKeys),
            projectIds: searchParams.getAll(URLParams.ProjectId),
            isMilestone: searchParams.get(URLParams.IsMilestone),
            targetDateStart: searchParams.get(URLParams.TargetDateStart),
            targetDateEnd: searchParams.get(URLParams.TargetDateEnd),
            departmentIds: searchParams.getAll(URLParams.DepartmentId),
            disciplineIds: searchParams.getAll(URLParams.DisciplineId),
            statusIds: searchParams.getAll(URLParams.StatusId),
            phaseIds: searchParams.getAll(URLParams.PhaseId),
            baselineIds: searchParams.getAll(URLParams.BaselineId),
            communityIds: searchParams.getAll(URLParams.CommunityId),
            taskTagIds: searchParams.getAll(URLParams.TagId)
        };
    }

    const params = Array.from(searchParams.values());
    const paramCount = params ? params.length : 0;

    return {
        reportId: reportId,
        savedFilterValues: savedFilterValues,
        urlParams: urlParams,
        paramCount,
        history: ownProps.history
    }
}

function mapDispatchToProps(dispatch) {
    return {
        reportsActions: bindActionCreators(reportsActions, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportViewPage);
